/**
 * HumanFigure — billboard scale-reference figure that fades out when the camera
 * gets close.
 */

import {
  MeshBuilder,
  StandardMaterial,
  Mesh,
  Vector3,
  type Scene,
  type Camera,
} from 'babylonjs';
import { textureFromBase64 } from '../utils/BabylonEditorUtils';
import type { EventBus } from '@shared/events/EventBus';
import type { EditorEvents } from '@shared/events/EventTypes';
import UserTexture from '../../../assets/User_Placeholder.png';

const FIGURE_HEIGHT = 1.65;

export class HumanFigure {
  readonly mesh: Mesh;
  private material: StandardMaterial;
  private camera: Camera;
  private targetAlpha = 1;

  constructor(
    scene: Scene,
    position: { x: number; y: number; z: number },
    events: EventBus<EditorEvents>,
    camera: Camera,
  ) {
    this.camera = camera;

    this.mesh = MeshBuilder.CreatePlane(
      'human_figure',
      { width: 0.7, height: FIGURE_HEIGHT },
      scene,
    );
    this.mesh.position = new Vector3(position.x, position.y + FIGURE_HEIGHT / 2, position.z);
    // Static rotation (NOT billboarded) — face the figure across the scene.
    this.mesh.rotation.y = Math.PI / 2;
    this.mesh.isPickable = false;

    this.material = new StandardMaterial('human_figure_mat', scene);
    this.material.disableLighting = true;
    this.material.backFaceCulling = false;
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.alpha = 0;
    this.mesh.material = this.material;

    textureFromBase64(UserTexture, scene)
      .then((texture) => {
        texture.hasAlpha = true;
        this.material.diffuseTexture = texture;
        this.material.emissiveTexture = texture;
        this.material.alpha = 1;
        events.emit('staticAssets:loaded', { type: 'figure' });
      })
      .catch(() => { /* ignore */ });

    scene.onBeforeRenderObservable.add(() => this._updateFade());
  }

  private _updateFade(): void {
    const cam = this.camera as Camera & { position: Vector3 };
    const distance = Vector3.Distance(cam.position, this.mesh.position);
    const target = distance < 3 ? 0 : 1;
    if (target !== this.targetAlpha) {
      this.targetAlpha = target;
    }
    // simple lerp toward target
    this.material.alpha += (this.targetAlpha - this.material.alpha) * 0.15;
  }

  cleanup(): void {
    this.material.diffuseTexture?.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}

export default HumanFigure;
